import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { SupabaseIcon } from '@/components/SupabaseIcon';
import WorkshopStepWrapper from '../WorkshopStepWrapper';
import { useWorkshopProgress } from '@/contexts/WorkshopProgressContext';
import { Trophy, CheckCircle, ArrowRight } from 'lucide-react';

export const WorkshopAssessment: React.FC = () => {
  const { state, navigateToStep, masterSkill } = useWorkshopProgress();

  const handleContinue = () => {
    masterSkill(4); // Professional Polish
    navigateToStep('cta');
  };

  return (
    <WorkshopStepWrapper
      step="assessment"
      title="Skills Assessment"
      description="Here's what you just mastered"
      showSkillsPreview={false}
      autoMarkComplete={true}
      requiredSkills={[4]}
    >
      {/* Achievement Celebration */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-full bg-gradient-to-r from-green-500 to-purple-500 mx-auto mb-4 flex items-center justify-center">
          <Trophy className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Congratulations! Workshop Complete
        </h2>
        <p className="text-xl text-gray-600">
          You've mastered key AI quality control techniques in just 5 minutes
        </p>
      </div>

      {/* Skills Mastery Checklist */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="text-center flex items-center justify-center">
            <SupabaseIcon
              iconPath="achievement-trophy.png"
              alt="Achievement"
              size="md"
              className="w-6 h-6 mr-2"
            />
            Your AI Mastery Competencies
          </CardTitle>
          <CardDescription className="text-center">Skills you demonstrated in this workshop</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {state.skills.map((skill, index) => (
              <div key={index} className="flex items-center space-x-3 p-4 bg-white rounded-xl border">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  skill.mastered ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {skill.mastered ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className={`font-semibold ${skill.mastered ? 'text-green-800' : 'text-gray-600'}`}>
                    {skill.skill}
                  </h4>
                  <p className={`text-sm ${skill.mastered ? 'text-green-600' : 'text-gray-500'}`}>
                    {skill.description}
                  </p>
                </div>
                {skill.mastered && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-600 border-green-300 hover:bg-green-50"
                  >
                    Mastered ✓
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Lyra's Assessment */}
      <div className="bg-white rounded-3xl shadow-lg p-8 mb-8">
        <div className="flex items-start space-x-6">
          <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center">
            <SupabaseIcon
              iconPath="lyra-avatar.png"
              alt="Lyra"
              size="lg"
              className="w-12 h-12"
            />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-purple-800 mb-3">Lyra's Professional Assessment</h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              "Outstanding work! You've demonstrated the core competencies that separate AI users from AI directors.
              You understand context setting, impact specification, voice alignment, and quality control.
              These are exactly the skills that transform AI from generic output into mission-aligned, professional communication."
            </p>
            <div className="bg-purple-50 border border-purple-200 p-4 rounded-xl">
              <div className="text-purple-800 font-semibold">Ready for Full Transformation?</div>
              <div className="text-purple-700 mt-2">This workshop was just the beginning. See how these skills scale across all your nonprofit work.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center">
        <Button
          onClick={handleContinue}
          size="lg"
          className="bg-purple-600 hover:bg-purple-700"
        >
          <ArrowRight className="w-5 h-5 mr-2" />
          Ready for Complete Professional Transformation
        </Button>
      </div>
    </WorkshopStepWrapper>
  );
};

export default WorkshopAssessment;